import {
  COMMON_COLORS,
  REFERENCE_LINE_ORIENTATION,
} from '@neteera/neteera-web-nextgen';
import dayjs from 'dayjs';
import { IntlShape, MessageDescriptor } from 'react-intl';
import { DateUTC } from 'src/types/utility';
import {
  FORMAT_WITHOUT_SECONDS,
  getFormatDateByContinent,
  GRAPH_DATE_FORMAT,
  GRAPH_DATE_TIME_FORMAT,
  TIME_24H_FORMAT,
} from 'src/utils/timeUtils';

enum LEGEND_ITEM_TYPE {
  LINE = 'line',
  DOT_LINE = 'dot-line',
}

export const getChartConfig = (
  intl: IntlShape,
  messages: Record<
    | 'heartRate'
    | 'hrBaseline'
    | 'respiratoryRate'
    | 'rrBaseline'
    | 'motion'
    | 'hr'
    | 'rr'
    | 'motionShort',
    MessageDescriptor
  >,
) => ({
  hr: {
    label: intl.formatMessage(messages.heartRate),
    shortLabel: intl.formatMessage(messages.hr),
    color: COMMON_COLORS.redDark,
  },
  hrBaseline: {
    label: intl.formatMessage(messages.hrBaseline),
    color: COMMON_COLORS.redDark,
  },
  rr: {
    label: intl.formatMessage(messages.respiratoryRate),
    shortLabel: intl.formatMessage(messages.rr),
    color: COMMON_COLORS.blueDark,
  },
  rrBaseline: {
    label: intl.formatMessage(messages.rrBaseline),
    color: COMMON_COLORS.blueDark,
  },
  motion: {
    label: intl.formatMessage(messages.motion),
    shortLabel: intl.formatMessage(messages.motionShort),
    color: COMMON_COLORS.yellowDark,
  },
  positionChange: {
    label: intl.formatMessage(messages.motion),
    color: COMMON_COLORS.teal400,
  },
  anchorLine: {
    color: 'transparent',
  },
});

export const getLeftAxis = (
  intl: IntlShape,
  messages: Record<'bpm', MessageDescriptor>,
) => ({
  yAxisId: 'hr',
  domain: [0, 160],
  label: intl.formatMessage(messages.bpm),
});

export const getRightAxis = (
  intl: IntlShape,
  messages: Record<'brpm', MessageDescriptor>,
) => ({
  yAxisId: 'rr',
  domain: [0, 40],
  label: intl.formatMessage(messages.brpm),
});

const getPositionChangeReferenceLines = (positionChanges: string[]) =>
  positionChanges.map(positionChange => ({
    dataKey: 'positionChange',
    value: positionChange,
    yAxisId: 'motion',
    orientation: REFERENCE_LINE_ORIENTATION.VERTICAL,
  }));

export const getReferenceLines = (
  hrBaseline: number | null,
  rrBaseline: number | null,
  positionChanges: string[],
) => [
  {
    dataKey: 'hrBaseline',
    value: hrBaseline,
    yAxisId: 'hr',
    orientation: REFERENCE_LINE_ORIENTATION.HORIZONTAL,
  },
  {
    dataKey: 'rrBaseline',
    value: rrBaseline,
    yAxisId: 'rr',
    orientation: REFERENCE_LINE_ORIENTATION.HORIZONTAL,
  },
  ...getPositionChangeReferenceLines(positionChanges),
];

export const trendLines = [
  {
    dataKey: 'motion',
    yAxisId: 'motion',
  },
  {
    dataKey: 'hr',
    yAxisId: 'hr',
  },
  {
    dataKey: 'rr',
    yAxisId: 'rr',
  },
  {
    dataKey: 'anchorLine',
    yAxisId: 'motion',
  },
];

export const getLegendConfig = (
  config: ReturnType<typeof getChartConfig>,
  intl: IntlShape,
  messages: Record<'positionChange', MessageDescriptor>,
  activeData: string | null,
  hiddenData: string[],
  stepSize: number,
  unit: string,
) => {
  const legendItems = [
    {
      dataKey: 'hr',
      label: config.hr.label,
      color: config.hr.color,
      type: LEGEND_ITEM_TYPE.LINE,
      className:
        (activeData && activeData !== 'hr') || hiddenData.includes('hr')
          ? 'line-through'
          : '',
    },
    {
      dataKey: 'hrBaseline',
      label: config.hrBaseline.label,
      color: config.hrBaseline.color,
      type: LEGEND_ITEM_TYPE.DOT_LINE,
      className:
        (activeData && activeData !== 'hrBaseline') ||
        hiddenData.includes('hrBaseline')
          ? 'line-through'
          : '',
    },
    {
      dataKey: 'rr',
      label: config.rr.label,
      color: config.rr.color,
      type: LEGEND_ITEM_TYPE.LINE,
      className:
        (activeData && activeData !== 'rr') || hiddenData.includes('rr')
          ? 'line-through'
          : '',
    },
    {
      dataKey: 'rrBaseline',
      label: config.rrBaseline.label,
      color: config.rrBaseline.color,
      type: LEGEND_ITEM_TYPE.DOT_LINE,
      className:
        (activeData && activeData !== 'rrBaseline') ||
        hiddenData.includes('rrBaseline')
          ? 'line-through'
          : '',
    },
  ];
  if ((stepSize <= 6 && unit === 'hour') || unit === 'minute') {
    legendItems.push({
      dataKey: 'motion',
      label: config.motion.label,
      color: config.motion.color,
      type: LEGEND_ITEM_TYPE.LINE,
      className:
        (activeData && activeData !== 'motion') || hiddenData.includes('motion')
          ? 'line-through'
          : '',
    });
  }

  if ((stepSize === 1 && unit === 'hour') || unit === 'minute') {
    legendItems.push({
      dataKey: 'positionChange',
      label: intl.formatMessage(messages.positionChange),
      color: COMMON_COLORS.teal400,
      type: LEGEND_ITEM_TYPE.LINE,
      className:
        (activeData && activeData !== 'positionChange') ||
        hiddenData.includes('positionChange')
          ? 'line-through'
          : '',
    });
  }

  return legendItems;
};

export const getTooltipConfig = (
  config: ReturnType<typeof getChartConfig>,
  intl: IntlShape,
  messages: Record<'respIeRatio' | 'respAmplitude', MessageDescriptor>,
) => {
  return {
    hr: {
      label: config.hr.shortLabel,
      color: config.hr.color,
    },
    rr: {
      label: config.rr.shortLabel,
      color: config.rr.color,
    },
    motion: {
      label: config.motion.shortLabel,
      color: config.motion.color,
    },
    ie: {
      label: intl.formatMessage(messages.respIeRatio),
      color: COMMON_COLORS.blueLight,
    },
    ra: {
      label: intl.formatMessage(messages.respAmplitude),
      color: COMMON_COLORS.blueMid,
    },
  };
};

const oneDayFormat = (value: DateUTC) => {
  const hour = dayjs.utc(value).format(TIME_24H_FORMAT);

  if (hour === '00:00') {
    return dayjs.utc(value).format(GRAPH_DATE_FORMAT);
  }

  return hour;
};

const multiDaysFormat = (value: DateUTC) =>
  dayjs.utc(value).format(getFormatDateByContinent(GRAPH_DATE_TIME_FORMAT));

export const xAxisTickFormatter = (
  value: DateUTC,
  stepSize: number,
  unit: string,
) => {
  if ((unit === 'hour' && stepSize <= 1) || unit === 'minutes') {
    return oneDayFormat(value);
  }

  return multiDaysFormat(value);
};

export const tooltipTimeFormatter = (value: string) =>
  dayjs.utc(value).format(getFormatDateByContinent(FORMAT_WITHOUT_SECONDS));
